import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { ApiError } from '../../utils/Api';

import {
  CreateAppParams,
  CreateAppResponse,
  RegenerateApiKeyParams,
  RegenerateApiKeyResponse,
  UpdateAppParams,
  useAppsService,
} from './apps.service';
import { AppModel } from './types';

export const appsListQueryKey = 'apps-list';
export const appsGetQueryKey = 'apps-get';

export const useAppsList = () => {
  const appsService = useAppsService();

  return useQuery({
    queryKey: [appsListQueryKey],
    queryFn: () => appsService.getApps(),
  });
};

export const useApp = (appId: string) => {
  const appsService = useAppsService();

  return useQuery({
    queryKey: [appsGetQueryKey, { appId }],
    queryFn: () => appsService.getApp(appId),
  });
};

export const useUpdateApp = () => {
  const appsService = useAppsService();
  const queryClient = useQueryClient();

  return useMutation<AppModel, AxiosError<ApiError>, UpdateAppParams>({
    mutationFn: (params: UpdateAppParams) => appsService.updateApp(params),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [appsListQueryKey] }),
        queryClient.invalidateQueries({ queryKey: [appsGetQueryKey] }),
      ]);
    },
  });
};

export const useDeleteApp = () => {
  const appsService = useAppsService();
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError<ApiError>, string>({
    mutationFn: (appId: string) => appsService.deleteApp(appId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [appsListQueryKey] }),
  });
};

export const useCreateApp = () => {
  const appsService = useAppsService();
  const queryClient = useQueryClient();

  return useMutation<CreateAppResponse, AxiosError<ApiError>, CreateAppParams>({
    mutationFn: (params: CreateAppParams) => appsService.createApp(params),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [appsListQueryKey] }),
  });
};

export const useRegenerateApiKey = () => {
  const appsService = useAppsService();

  return useMutation<RegenerateApiKeyResponse, AxiosError, RegenerateApiKeyParams>({
    mutationFn: (params: RegenerateApiKeyParams) => appsService.regenerateApiKey(params),
  });
};
